import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./BlogPost.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeSlug from "rehype-slug";
import rehypeHighlight from "rehype-highlight";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import java from "highlight.js/lib/languages/java";
import kotlin from "highlight.js/lib/languages/kotlin";
import javascript from "highlight.js/lib/languages/javascript";
import typescript from "highlight.js/lib/languages/typescript";
import bash from "highlight.js/lib/languages/bash";
import json from "highlight.js/lib/languages/json";
import xml from "highlight.js/lib/languages/xml";
import yaml from "highlight.js/lib/languages/yaml";
import sql from "highlight.js/lib/languages/sql";
import css from "highlight.js/lib/languages/css";
import {
  getMarkdownPreview,
  parseMarkdownWithFrontMatter,
} from "../../utils/markdown";
import { GITHUB_PROFILE_README_URL } from "../../config/blog";
import BlogVoiceReader from "./BlogVoiceReader";
import BlogReadingThemePicker from "./BlogReadingThemePicker";
import { BLOG_READING_THEMES } from "../../constants/blogReadingThemes";
import { useBlogReadingTheme } from "../../hooks/useBlogReadingTheme";
import {
  applyDisplayTermOverrides,
  getBlogPostRawUrl,
  getFolderDisplayName,
  humanizeName,
  loadBlogIndex,
} from "../../utils/blogIndex";
import {
  BLOG_INDEX_PATH,
  buildPostIndex,
  getPostSlug,
  getPostUrl,
  normalizeInternalPath,
  slugifyTitle,
} from "../../utils/routes";

const CATEGORY_PREVIEW_FALLBACKS = {
  "About Me":
    "A quick overview of my background, skills, projects, and experience.",
  Android:
    "Practical Android notes with examples, patterns, and best practices.",
  Cloud: "Deployment notes, setup steps, and troubleshooting guidance.",
  "Dev Concepts": "Simple explanations with real-world software examples.",
  DevOps: "Practical DevOps notes, commands, and workflow guidance.",
  "Spring Boot":
    "Backend development notes with examples and implementation details.",
};

function getFallbackPreview(category) {
  return (
    CATEGORY_PREVIEW_FALLBACKS[category] ||
    "Practical notes, examples, and key concepts for this topic."
  );
}

const STATIC_FOLDER_STRUCTURE = [
  {
    name: "About Me",
    type: "folder",
    children: [
      {
        name: "About Me",
        type: "file",
        content: GITHUB_PROFILE_README_URL,
        githubPath: "README.md",
        preview: getFallbackPreview("About Me"),
      },
    ],
  },
];

const FILE_TITLE_OVERRIDES = {
  "AndroidInterviewQuestionsSecurityBestPractices.md":
    "Android Interview Questions - Security Best Practices",
  "GithubActions.md": "GitHub Actions",
  "LinuxGuide.md": "Linux Guide",
  "SQL-NOTES.md": "SQL Guide",
  "SQLInterviewQuestions.md": "SQL Interview Questions",
  "Docker.md": "Docker",
  "DockerBackup.md": "Docker Backup",
  "DockerBackup2.md": "Docker Backup 2",
  "Kubernetes.md": "Kubernetes",
  "springSecurity.md": "Spring Security",
  "springboot.md": "Spring Boot",
  "springdata.md": "Spring Data",
  "part-1-architecture-and-deployment-strategy.md":
    "Part 1 - Architecture and Deployment Strategy",
  "part-2-backend-deployment-on-aws-lightsail.md":
    "Part 2 - Backend Deployment on AWS Lightsail",
  "part-3-frontend-domains-ssl-troubleshooting.md":
    "Part 3 - Frontend, Domains, SSL and Troubleshooting",
};

const SIDEBAR_COLLAPSED_STORAGE_KEY = "blogSidebarCollapsed";
const SIDEBAR_WIDTH_STORAGE_KEY = "portfolio_blog_sidebar_width";
const BLOG_READING_THEATER_MODE_STORAGE_KEY = "blogReadingTheaterMode";
const MIN_SIDEBAR_WIDTH = 220;
const DEFAULT_SIDEBAR_WIDTH = 300;
const NOTES_CATEGORY = "Notes";
const NOTES_CATEGORY_SLUG = "notes";
const NOTES_UNLOCK_STORAGE_KEY = "portfolio_notes_unlocked";
// Frontend-only soft-lock placeholder. Override at build time with
// REACT_APP_NOTES_PASSWORD; CRA exposes REACT_APP_* values in the browser.
const NOTES_PASSWORD =
  process.env.REACT_APP_NOTES_PASSWORD || "notes-dev-password";
const SOURCE_NOTE_EXTENSIONS = new Set([
  ".java",
  ".kt",
  ".js",
  ".json",
  ".xml",
  ".css",
  ".html",
  ".txt",
]);
const LANGUAGE_BY_EXTENSION = {
  ".md": "markdown",
  ".mdx": "markdown",
  ".java": "java",
  ".kt": "kotlin",
  ".js": "javascript",
  ".json": "json",
  ".xml": "xml",
  ".css": "css",
  ".html": "html",
  ".txt": "text",
};

const getMaxSidebarWidth = () => {
  if (typeof window === "undefined") return 480;

  const viewportWidth = window.innerWidth;

  if (viewportWidth >= 1800) return 800;
  if (viewportWidth >= 1400) return 700;
  if (viewportWidth >= 1200) return 620;
  return 480;
};

const clampSidebarWidth = (width) =>
  Math.min(Math.max(width, MIN_SIDEBAR_WIDTH), getMaxSidebarWidth());

const shouldUseDefaultSidebarWidth = (width) =>
  typeof window !== "undefined" &&
  window.innerWidth < 1200 &&
  width > DEFAULT_SIDEBAR_WIDTH;

const getSafeSidebarWidth = (width) =>
  shouldUseDefaultSidebarWidth(width)
    ? DEFAULT_SIDEBAR_WIDTH
    : clampSidebarWidth(width);

const normalizeGitHubPath = (path) =>
  path
    .split("/")
    .filter((part) => part && part !== ".")
    .reduce((parts, part) => {
      if (part === "..") {
        parts.pop();
      } else {
        parts.push(part);
      }

      return parts;
    }, [])
    .join("/");

const resolveRelativeGitHubPath = (currentPath, targetPath) => {
  if (
    !currentPath ||
    !targetPath ||
    /^(?:[a-z][a-z\d+.-]*:|#|\/)/i.test(targetPath)
  ) {
    return "";
  }

  const targetWithoutFragment = targetPath.split(/[?#]/)[0] || "";
  let cleanTargetPath = targetWithoutFragment;

  try {
    cleanTargetPath = decodeURIComponent(targetWithoutFragment);
  } catch {
    cleanTargetPath = targetWithoutFragment;
  }

  const currentFolder = currentPath.includes("/")
    ? currentPath.substring(0, currentPath.lastIndexOf("/") + 1)
    : "";

  return normalizeGitHubPath(`${currentFolder}${cleanTargetPath}`);
};

const getFileDisplayName = (fileName) =>
  FILE_TITLE_OVERRIDES[fileName] || humanizeName(fileName);

const getExtensionFromPath = (path = "") =>
  String(path).match(/(\.[^./?#]+)(?:[?#].*)?$/)?.[1]?.toLowerCase() || "";

const getPostExtension = (post = {}) =>
  String(post.extension || getExtensionFromPath(post.githubPath || post.path))
    .trim()
    .toLowerCase();

const valueEndsWithPdf = (value) =>
  /\.pdf(?:[?#].*)?$/i.test(String(value || "").trim());

const getPostLanguage = (post = {}) =>
  String(
    post.language || LANGUAGE_BY_EXTENSION[getPostExtension(post)] || "text",
  )
    .trim()
    .toLowerCase();

const isMarkdownContentPost = (post = {}) => {
  const extension = getPostExtension(post);
  return post.type === "markdown" || extension === ".md" || extension === ".mdx";
};

const isSourceContentPost = (post = {}) =>
  SOURCE_NOTE_EXTENSIONS.has(getPostExtension(post));

const isPdfContentPost = (post = {}) =>
  post.type === "pdf" ||
  getPostExtension(post) === ".pdf" ||
  [
    post.path,
    post.githubPath,
    post.title,
    post.name,
    post.content,
    post.rawUrl,
    post.pdfUrl,
  ].some(valueEndsWithPdf);

const sortByName = (items) =>
  [...items].sort((first, second) => first.name.localeCompare(second.name));

const insertFileIntoFolderTree = (folders, pathParts, file) => {
  if (pathParts.length === 0) return;

  const [folderName, ...remainingParts] = pathParts;
  const displayFolderName = getFolderDisplayName(folderName);

  let folder = folders.find(
    (item) =>
      item.type === "folder" &&
      item.githubFolderPath ===
        pathParts.slice(0, pathParts.length - remainingParts.length).join("/"),
  );

  if (!folder) {
    folder = {
      name: displayFolderName,
      type: "folder",
      githubFolderPath: pathParts
        .slice(0, pathParts.length - remainingParts.length)
        .join("/"),
      children: [],
    };

    folders.push(folder);
  }

  if (remainingParts.length === 0) {
    folder.children.push(file);
    folder.children = sortByName(folder.children);
    return;
  }

  insertFileIntoFolderTree(folder.children, remainingParts, file);
  folder.children = sortByName(folder.children);
};

const buildFolderStructureFromIndex = (indexedPosts) => {
  const folders = [];

  indexedPosts.forEach((post) => {
    const pathParts = post.path.split("/");
    const fileName = pathParts.pop();

    if (!fileName || pathParts.length === 0) {
      return;
    }

    const isNote = post.isNote === true;
    const file = {
      name: isNote ? fileName : post.title || getFileDisplayName(fileName),
      title: post.title || getFileDisplayName(fileName),
      type: "file",
      content: post.rawUrl || getBlogPostRawUrl(post.path),
      githubPath: post.path,
      slug: post.slug,
      preview: post.summary || getFallbackPreview(post.folderGroup),
      summary: post.summary,
      date: post.date,
      categories: post.categories,
      displayCategory: post.categoryLabel,
      readTime: post.readTime,
      isNote,
      rawUrl: post.rawUrl,
      sourceType: post.type,
      extension: post.extension || getExtensionFromPath(post.path),
      language: post.language,
    };

    insertFileIntoFolderTree(folders, pathParts, file);
  });

  return sortByName(folders);
};

const stripMarkdown = (md) => {
  return md
    .replace(/!\[(.*?)\]\(.*?\)/g, "$1") // Keep alt text for images
    .replace(/\[(.*?)\]\(.*?\)/g, "$1") // Keep link text
    .replace(/[`*_>#\-~]/g, "")
    .replace(/\n{2,}/g, "\n")
    .replace(/#{1,6}\s/g, "")
    .trim();
};

const TITLE_WORD_OVERRIDES = {
  api: "API",
  apis: "APIs",
  aws: "AWS",
  ble: "BLE",
  ci: "CI",
  cd: "CD",
  css: "CSS",
  db: "DB",
  devops: "DevOps",
  docker: "Docker",
  github: "GitHub",
  html: "HTML",
  http: "HTTP",
  https: "HTTPS",
  iam: "IAM",
  iot: "IoT",
  java: "Java",
  javascript: "JavaScript",
  js: "JS",
  json: "JSON",
  jwt: "JWT",
  kubernetes: "Kubernetes",
  linux: "Linux",
  md: "Markdown",
  mvvm: "MVVM",
  oauth: "OAuth",
  rest: "REST",
  sdk: "SDK",
  sql: "SQL",
  ssl: "SSL",
  tls: "TLS",
  ui: "UI",
  url: "URL",
  ux: "UX",
  yaml: "YAML",
};

const toTitleCaseSmart = (value) =>
  value
    .split(" ")
    .filter(Boolean)
    .map((word) => {
      const normalized = word.toLowerCase();
      return (
        TITLE_WORD_OVERRIDES[normalized] ||
        word.charAt(0).toUpperCase() + word.slice(1)
      );
    })
    .join(" ");

const cleanDisplayTitle = (
  rawTitle,
  fallbackTitle = "Untitled",
  options = {},
) => {
  const fallback = fallbackTitle || "Untitled";
  if (!rawTitle) return fallback;

  let title = String(rawTitle)
    .replace(/^\uFEFF/, "")
    .trim();

  // Handle Markdown link-style headings:
  const markdownLinkMatch = title.match(/^\[(.+?)\]\((.*?)\)$/);
  if (markdownLinkMatch) {
    title = markdownLinkMatch[1].trim();
  }

  // Remove common Markdown wrappers.
  title = title
    .replace(/^`(.+?)`$/, "$1")
    .replace(/^\*\*(.+?)\*\*$/, "$1")
    .replace(/^__(.+?)__$/, "$1")
    .replace(/^_(.+?)_$/, "$1")
    .replace(/^\*(.+?)\*$/, "$1")
    .trim();

  // If the title is still a path or URL, keep only the file/final segment.
  title = title.split(/[?#]/)[0].split("/").filter(Boolean).pop() || title;

  // Remove Markdown extension and clean common filename separators.
  title = title
    .replace(/\.(md|mdx)$/i, "")
    .replace(/\s+/g, " ")
    .trim();

  // Preserve " VS " as readable " VS " before camel-case splitting.
  title = title.replace(/\bvs\b/gi, "VS");

  if (options.preserveCasing) {
    return applyDisplayTermOverrides(title);
  }

  // Split PascalCase/camelCase, including acronym-to-word boundaries.
  // GithubActions -> Github Actions
  title = title
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!title) return fallback;

  return applyDisplayTermOverrides(toTitleCaseSmart(title));
};

const markdownLanguages = {
  java,
  kotlin,
  javascript,
  typescript,
  bash,
  json,
  xml,
  yaml,
  sql,
  css,
};

const markdownHighlightOptions = {
  detect: false,
  languages: markdownLanguages,
  aliases: {
    js: "javascript",
    jsx: "javascript",
    ts: "typescript",
    tsx: "typescript",
    shell: "bash",
    sh: "bash",
    kt: "kotlin",
    html: "xml",
    yml: "yaml",
  },
  plainText: ["text", "txt", "plaintext"],
};

const blogLayoutClassNamePattern =
  /^(blog-image-grid|blog-image-grid-2|blog-image-grid-3|blog-image-wide|blog-image-compact|blog-figure)$/;

const markdownSanitizeSchema = {
  ...defaultSchema,
  clobberPrefix: "",
  tagNames: [...(defaultSchema.tagNames || []), "div", "figure", "figcaption"],
  attributes: {
    ...defaultSchema.attributes,
    code: [
      ...(defaultSchema.attributes.code || []),
      ["className", /^language-/],
    ],
    h1: [...(defaultSchema.attributes.h1 || []), "id"],
    h2: [...(defaultSchema.attributes.h2 || []), "id"],
    h3: [...(defaultSchema.attributes.h3 || []), "id"],
    h4: [...(defaultSchema.attributes.h4 || []), "id"],
    h5: [...(defaultSchema.attributes.h5 || []), "id"],
    h6: [...(defaultSchema.attributes.h6 || []), "id"],
    span: [...(defaultSchema.attributes.span || []), "style"],
    div: [
      ...(defaultSchema.attributes.div || []),
      ["className", blogLayoutClassNamePattern],
    ],
    figure: [
      ...(defaultSchema.attributes.figure || []),
      ["className", blogLayoutClassNamePattern],
    ],
    figcaption: [
      ...(defaultSchema.attributes.figcaption || []),
      ["className", blogLayoutClassNamePattern],
    ],
    img: [
      ...(defaultSchema.attributes.img || []),
      ["className", blogLayoutClassNamePattern],
    ],
  },
};

const getSafeInlineColor = (style) => {
  if (!style) return undefined;

  if (typeof style === "object" && style.color) {
    return /^#[\da-f]{3}(?:[\da-f]{3})?$/i.test(style.color)
      ? style.color
      : undefined;
  }

  const colorMatch = String(style).match(
    /(?:^|;)\s*color\s*:\s*(#[\da-f]{3}(?:[\da-f]{3})?)\s*(?:;|$)/i,
  );

  return colorMatch?.[1];
};

const isRemoteMarkdown = (content) =>
  typeof content === "string" && /^https?:\/\//i.test(content);

const getRemoteBaseUrl = (content) =>
  isRemoteMarkdown(content)
    ? content.substring(0, content.lastIndexOf("/") + 1)
    : "";

const resolveMarkdownUrl = (url, baseUrl) => {
  if (!url || !baseUrl || /^(?:[a-z][a-z\d+.-]*:|#|\/)/i.test(url)) {
    return url;
  }

  try {
    return new URL(url, baseUrl).href;
  } catch {
    return url;
  }
};

const scrollToArticleHash = (hash, contentRoot) => {
  if (!hash || !hash.startsWith("#")) return false;

  const rawId = hash.slice(1);
  if (!rawId) return false;

  let targetId = rawId;

  try {
    targetId = decodeURIComponent(rawId);
  } catch {
    targetId = rawId;
  }

  const root = contentRoot || document;
  const target = Array.from(root.querySelectorAll("[id]")).find(
    (element) => element.id === targetId,
  );

  if (!target) return false;

  target.scrollIntoView({ behavior: "smooth", block: "start" });

  try {
    window.history.pushState(
      null,
      "",
      `${window.location.pathname}${window.location.search}#${encodeURIComponent(targetId)}`,
    );
  } catch {
    window.location.hash = targetId;
  }

  return true;
};

const extractText = (children) =>
  React.Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") {
        return String(child);
      }

      if (React.isValidElement(child)) {
        return extractText(child.props.children);
      }

      return "";
    })
    .join("");

const getCategoryFromPath = (path) => path.split("/")[0] || "Uncategorized";

const getNormalizedSlug = (value) => slugifyTitle(value || "");

const isNotesCategoryValue = (value) =>
  getNormalizedSlug(value) === NOTES_CATEGORY_SLUG;

const isNotesPath = (path) => {
  const normalizedPath = normalizeInternalPath(path || "");
  return (
    normalizedPath === NOTES_CATEGORY_SLUG ||
    normalizedPath.startsWith(`${NOTES_CATEGORY_SLUG}/`)
  );
};

const isNotesPost = (post = {}) => {
  if (post.isNote === true) {
    return true;
  }

  const categoryValues = [
    post.category,
    post.displayCategory,
    post.categoryLabel,
    post.folderGroup,
    ...(Array.isArray(post.categories) ? post.categories : []),
  ];

  return (
    categoryValues.some(isNotesCategoryValue) ||
    isNotesPath(post.githubPath || post.path || post.internalPath || post.name)
  );
};

const postMatchesCategory = (post, category) =>
  isNotesCategoryValue(category)
    ? isNotesPost(post)
    : post.category === category;

const isNotesTreeItem = (item = {}, path = "") => {
  const categoryValues = [
    item.name,
    item.category,
    item.displayCategory,
    ...(Array.isArray(item.categories) ? item.categories : []),
  ];

  return (
    categoryValues.some(isNotesCategoryValue) ||
    isNotesPath(item.githubPath || item.path || item.githubFolderPath || path)
  );
};

const pruneLockedNotesTree = (items = [], notesUnlocked, currentPath = "") =>
  items
    .map((item) => {
      const itemPath = currentPath ? `${currentPath}/${item.name}` : item.name;
      const itemIsNotes = isNotesTreeItem(item, itemPath);

      if (notesUnlocked) {
        return item.children
          ? {
              ...item,
              children: pruneLockedNotesTree(
                item.children,
                notesUnlocked,
                itemPath,
              ),
            }
          : item;
      }

      if (itemIsNotes) {
        return item.type === "folder" || item.children
          ? { ...item, children: [] }
          : null;
      }

      if (!item.children) {
        return item;
      }

      return {
        ...item,
        children: pruneLockedNotesTree(item.children, notesUnlocked, itemPath),
      };
    })
    .filter(Boolean);

const getExpandedFoldersForPath = (filePath) => {
  if (!filePath) return {};

  const segments = filePath.split("/");
  const expanded = {};

  for (let index = 1; index < segments.length; index += 1) {
    expanded[segments.slice(0, index).join("/")] = true;
  }

  return expanded;
};

const buildCacheBustedUrl = (url) => {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}t=${Date.now()}`;
};

const extractLeadingH1 = (markdown) => {
  const lines = markdown.split(/\r?\n/);
  const firstContentIndex = lines.findIndex((line) => line.trim() !== "");

  if (firstContentIndex === -1) {
    return { title: "", markdown };
  }

  const firstContentLine = lines[firstContentIndex].trim();
  const h1Match = /^#(?!#)\s+(.+?)\s*#*\s*$/.exec(firstContentLine);

  if (!h1Match) {
    return { title: "", markdown };
  }

  const title = h1Match[1].trim();
  const markdownWithoutTitle = lines
    .slice(firstContentIndex + 1)
    .join("\n")
    .replace(/^\n+/, "");

  return {
    title,
    markdown: markdownWithoutTitle,
  };
};

const buildPostFromMarkdown = (
  basePost,
  rawMarkdown,
  { includeMarkdown = true } = {},
) => {
  const parsedMarkdown = parseMarkdownWithFrontMatter(rawMarkdown);
  const { title: markdownTitle, markdown: markdownBody } = extractLeadingH1(
    parsedMarkdown.content,
  );
  const previewSource = markdownBody || parsedMarkdown.content;
  const previewText = stripMarkdown(previewSource).slice(0, 150);
  const fallbackTitle =
    basePost.title ||
    getFileDisplayName(basePost.githubPath?.split("/").pop() || "Untitled");
  const metadataTitle = parsedMarkdown.metadata.title;
  const metadataSlug = parsedMarkdown.metadata.slug;
  const metadataSummary = parsedMarkdown.metadata.summary;
  const metadataDescription = parsedMarkdown.metadata.description;
  const metadataDate = parsedMarkdown.metadata.date;
  const metadataTags = Array.isArray(parsedMarkdown.metadata.tags)
    ? parsedMarkdown.metadata.tags
    : [];
  const metadataCategories = Array.isArray(parsedMarkdown.metadata.categories)
    ? parsedMarkdown.metadata.categories
    : [];
  const metadataReadTime = parsedMarkdown.metadata.readTime;
  const authoredTitle = metadataTitle || markdownTitle;
  const preview = getMarkdownPreview(
    parsedMarkdown.metadata,
    previewText,
    basePost.preview,
  );
  const title = authoredTitle
    ? cleanDisplayTitle(authoredTitle, fallbackTitle, {
        preserveCasing: true,
      })
    : cleanDisplayTitle(fallbackTitle);
  const slug =
    basePost.slug ||
    slugifyTitle(metadataSlug) ||
    slugifyTitle(authoredTitle) ||
    slugifyTitle(fallbackTitle);

  return {
    ...basePost,
    title,
    slug,
    markdownTitle,
    markdown: includeMarkdown ? markdownBody : "",
    preview,
    wordCount: markdownBody.trim() ? markdownBody.trim().split(/\s+/).length : 0,
    description: metadataDescription || "",
    summary: metadataSummary || basePost.summary || "",
    date: metadataDate || basePost.date || "",
    tags: metadataTags,
    categories: metadataCategories.length
      ? metadataCategories
      : basePost.categories || [],
    displayCategory: metadataCategories[0] || basePost.displayCategory || "",
    readTime: metadataReadTime || basePost.readTime || null,
    hasFrontMatter: parsedMarkdown.hasFrontMatter,
  };
};

const buildLoadedPostFromMarkdown = (basePost, rawMarkdown) =>
  buildPostFromMarkdown(basePost, rawMarkdown, { includeMarkdown: true });

const buildLoadedPostFromSource = (basePost, source) => ({
  ...basePost,
  source,
  contentMode: "source",
  language: getPostLanguage(basePost),
  wordCount: source.trim() ? source.trim().split(/\s+/).length : 0,
});

const buildLoadedPostFromPdf = (basePost) => ({
  ...basePost,
  contentMode: "pdf",
  pdfUrl: basePost.rawUrl || basePost.content,
  wordCount: null,
});

const flattenFolderFiles = (folder, category, currentPath = folder.name) => {
  let files = [];

  if (folder.children) {
    folder.children.forEach((item) => {
      const itemPath = `${currentPath}/${item.name}`;

      if (item.type === "file") {
        files.push({ ...item, category: category, path: itemPath });
      } else if (item.type === "folder") {
        files = files.concat(flattenFolderFiles(item, category, itemPath));
      }
    });
  }

  return files;
};

const flattenFiles = (structure) => {
  let files = [];

  structure.forEach((item) => {
    if (item.type === "folder" || item.children) {
      files = files.concat(flattenFolderFiles(item, item.name, item.name));
    }
  });

  return files;
};

const isShareableBlogPost = (post) =>
  post?.githubPath && post.content !== GITHUB_PROFILE_README_URL;

const CodeBlock = ({ children, codeClassName, language }) => {
  const [copied, setCopied] = useState(false);
  const codeText = extractText(children).replace(/\n$/, "");
  const languageLabel = language || "text";

  const copyCode = async () => {
    if (!navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(codeText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="code-block">
      <div className="code-block-header">
        <span>{languageLabel}</span>
        <button type="button" className="copy-button" onClick={copyCode}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre>
        <code className={codeClassName || `hljs language-${languageLabel}`}>
          {children}
        </code>
      </pre>
    </div>
  );
};

const MarkdownRenderer = ({
  markdown,
  baseUrl,
  currentGithubPath,
  contentRootRef,
  markdownPathLookup,
  onOpenMarkdownPath,
  notesUnlocked,
}) => {
  const components = useMemo(
    () => ({
      a({ href, children }) {
        const isSamePageAnchor =
          typeof href === "string" && href.startsWith("#");
        const relativeMarkdownPath =
          typeof href === "string" && !isSamePageAnchor
            ? resolveRelativeGitHubPath(currentGithubPath, href)
            : "";
        const linkedMarkdownPost = relativeMarkdownPath
          ? markdownPathLookup[relativeMarkdownPath.toLowerCase()]
          : null;
        const linkedPostIsLockedNote =
          linkedMarkdownPost &&
          isNotesPost(linkedMarkdownPost) &&
          !notesUnlocked;
        const resolvedHref = resolveMarkdownUrl(href, baseUrl);
        const isExternal =
          /^https?:\/\//i.test(resolvedHref || "") && !linkedMarkdownPost;

        return (
          <a
            href={resolvedHref}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            onClick={(event) => {
              if (isSamePageAnchor) {
                event.preventDefault();
                scrollToArticleHash(href, contentRootRef.current);
                return;
              }

              if (linkedMarkdownPost) {
                event.preventDefault();
                onOpenMarkdownPath(
                  relativeMarkdownPath,
                  linkedPostIsLockedNote ? "" : href.split("#")[1],
                );
              }
            }}
          >
            {children}
          </a>
        );
      },
      img({ src, alt, title, className }) {
        return (
          <img
            src={resolveMarkdownUrl(src, baseUrl)}
            alt={alt || ""}
            title={title}
            className={className}
            loading="lazy"
          />
        );
      },
      pre({ children }) {
        const codeElement = React.Children.toArray(children).find((child) =>
          React.isValidElement(child),
        );

        if (!codeElement) {
          return <pre>{children}</pre>;
        }

        const className = codeElement.props.className || "";
        const languageMatch = /language-([\w-]+)/.exec(className);
        const language = languageMatch?.[1] || "text";

        return (
          <CodeBlock codeClassName={className} language={language}>
            {codeElement.props.children}
          </CodeBlock>
        );
      },
      code({ className = "", children }) {
        return <code className={className}>{children}</code>;
      },
      span({ className, style, children }) {
        const color = getSafeInlineColor(style);
        const safeClassName =
          typeof className === "string" && /^hljs[\w\s-]*$/.test(className)
            ? className
            : undefined;

        return (
          <span className={safeClassName} style={color ? { color } : undefined}>
            {children}
          </span>
        );
      },
    }),
    [
      baseUrl,
      contentRootRef,
      currentGithubPath,
      markdownPathLookup,
      notesUnlocked,
      onOpenMarkdownPath,
    ],
  );

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[
        rehypeRaw,
        rehypeSlug,
        [rehypeSanitize, markdownSanitizeSchema],
        [rehypeHighlight, markdownHighlightOptions],
      ]}
      components={components}
    >
      {markdown}
    </ReactMarkdown>
  );
};

const SourceRenderer = ({ source, language }) => (
  <CodeBlock
    codeClassName={`hljs language-${language || "text"}`}
    language={language}
  >
    {source}
  </CodeBlock>
);

const PdfRenderer = ({ title, url }) => {
  const [blobPdfUrl, setBlobPdfUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!url) {
      setBlobPdfUrl("");
      setError("Unable to preview this PDF here.");
      setIsLoading(false);
      return undefined;
    }

    const abortController = new AbortController();
    let objectUrl = "";

    const loadPdf = async () => {
      setIsLoading(true);
      setError("");
      setBlobPdfUrl("");

      try {
        const response = await fetch(url, {
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`PDF request failed: ${response.status}`);
        }

        const blob = await response.blob();
        const pdfBlob =
          blob.type === "application/pdf"
            ? blob
            : new Blob([blob], { type: "application/pdf" });

        objectUrl = URL.createObjectURL(pdfBlob);
        setBlobPdfUrl(objectUrl);
      } catch (loadError) {
        if (loadError.name === "AbortError") return;
        setError("Unable to preview this PDF here.");
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      abortController.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [url]);

  return (
    <div className="pdf-viewer">
      {isLoading && (
        <div className="blog-loading blog-loading-inline" role="status">
          <div className="loading-spinner"></div>
          <p>Loading PDF...</p>
        </div>
      )}
      {error && <p className="blog-error-message">{error}</p>}
      {blobPdfUrl && !error && (
        <iframe
          className="pdf-viewer-frame blog-pdf-frame"
          src={blobPdfUrl}
          title={title ? `${title} PDF viewer` : "PDF viewer"}
          loading="lazy"
        />
      )}
      {url && (
        <p className="pdf-viewer-fallback">
          PDF not visible?{" "}
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open PDF in new tab
          </a>
          .
        </p>
      )}
    </div>
  );
};

const BlogPost = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { postSlug } = useParams();
  const [folderStructure, setFolderStructure] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postLoading, setPostLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [postError, setPostError] = useState("");
  const [loadRetryNonce, setLoadRetryNonce] = useState(0);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true"
    );
  });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_SIDEBAR_WIDTH;

    const storedWidth = Number(
      window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY),
    );

    return Number.isFinite(storedWidth) && storedWidth > 0
      ? getSafeSidebarWidth(storedWidth)
      : DEFAULT_SIDEBAR_WIDTH;
  });
  const [isTheaterMode, setIsTheaterMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      window.localStorage.getItem(BLOG_READING_THEATER_MODE_STORAGE_KEY) ===
      "true"
    );
  });
  const [sidebarMaxWidth, setSidebarMaxWidth] = useState(getMaxSidebarWidth);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [allPosts, setAllPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null); // State to hold active category filter
  const [notFoundMessage, setNotFoundMessage] = useState("");
  const [notesUnlocked, setNotesUnlocked] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem(NOTES_UNLOCK_STORAGE_KEY) === "true";
  });
  const [notesPasswordInput, setNotesPasswordInput] = useState("");
  const [notesUnlockError, setNotesUnlockError] = useState("");
  const activePostRequestRef = useRef(0);
  const contentRootRef = useRef(null);
  const sidebarRef = useRef(null);
  const pendingArticleHashRef = useRef("");
  const postCacheRef = useRef({});
  const { selectedTheme, selectedThemeId, setSelectedThemeId } =
    useBlogReadingTheme();

  const createPostMetadata = useCallback((file) => {
    const fileName =
      file.githubPath?.split("/").pop() || file.name || "Untitled";

    return {
      id: file.content,
      title: file.title || file.name || getFileDisplayName(fileName),
      markdown: file.markdown || "",
      baseUrl: getRemoteBaseUrl(file.content),
      preview:
        file.preview || "Open to read this documentation article from GitHub.",
      wordCount: file.wordCount || null,
      description: file.description || "",
      summary: file.summary || "",
      date: file.date || "",
      tags: file.tags || [],
      categories: Array.isArray(file.categories)
        ? file.categories
        : [file.categories].filter(Boolean),
      displayCategory: file.displayCategory || "",
      readTime: file.readTime || null,
      hasFrontMatter: file.hasFrontMatter || false,
      category: file.category,
      path: file.path,
      content: file.content,
      githubPath: file.githubPath,
      isNote: file.isNote === true,
      rawUrl: file.rawUrl || "",
      type: file.sourceType || file.type || "",
      extension: file.extension || getExtensionFromPath(file.githubPath),
      language: file.language || "",
      contentMode: file.contentMode || "",
      source: file.source || "",
      pdfUrl: file.pdfUrl || "",
      slug: getPostSlug({
        slug: file.slug,
        title: file.title || file.name,
        githubPath: file.githubPath,
        path: file.path,
        name: file.name,
      }),
    };
  }, []);

  const markdownPathLookup = useMemo(
    () =>
      allPosts.reduce((lookup, post) => {
        if (post.githubPath && post.content !== GITHUB_PROFILE_README_URL) {
          lookup[post.githubPath.toLowerCase()] = post;
        }

        return lookup;
      }, {}),
    [allPosts],
  );

  const postIndex = useMemo(() => buildPostIndex(allPosts), [allPosts]);

  const notesAreLocked = !notesUnlocked;

  const handleNotesUnlock = (event) => {
    event.preventDefault();

    if (notesPasswordInput === NOTES_PASSWORD) {
      setNotesUnlocked(true);
      setNotesUnlockError("");
      setNotesPasswordInput("");
      window.sessionStorage.setItem(NOTES_UNLOCK_STORAGE_KEY, "true");
      return;
    }

    setNotesUnlockError("Incorrect password. Please try again.");
  };

  const getRequestedPostKey = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const postParam = params.get("post");

    if (postParam) {
      try {
        return decodeURIComponent(postParam).trim();
      } catch {
        return postParam.trim();
      }
    }

    return postSlug || "";
  }, [location.search, postSlug]);

  const findPostFromUrlKey = useCallback(
    (postKey) => {
      if (!postKey) return null;

      const normalizedKey = normalizeInternalPath(postKey);
      const slugKey = slugifyTitle(postKey.replace(/^\/+|\/+$/g, ""));

      return (
        postIndex.byInternalPath[normalizedKey] ||
        postIndex.bySlug[slugKey] ||
        null
      );
    },
    [postIndex],
  );

  const updateBlogUrl = useCallback(
    (post, options = {}) => {
      if (!post?.githubPath) return;

      navigate(
        {
          pathname: getPostUrl(post),
          search: "",
        },
        { replace: options.replace === true },
      );
    },
    [navigate],
  );

  const clearBlogUrl = useCallback(
    (options = {}) => {
      if (
        location.pathname.toLowerCase() === BLOG_INDEX_PATH &&
        !location.search
      )
        return;

      navigate(
        {
          pathname: BLOG_INDEX_PATH,
          search: "",
          hash: "",
        },
        { replace: options.replace === true },
      );
    },
    [location.pathname, location.search, navigate],
  );

  useEffect(() => {
    let isMounted = true;

    const loadBlogIndexData = async () => {
      setLoading(true);
      setLoadError("");

      try {
        const indexPosts = await loadBlogIndex({
          force: loadRetryNonce > 0,
        });
        const githubFolders = buildFolderStructureFromIndex(indexPosts);
        const nextFolderStructure = [
          ...STATIC_FOLDER_STRUCTURE,
          ...githubFolders,
        ];
        const flatFiles = flattenFiles(nextFolderStructure);
        const posts = flatFiles.map((file) => createPostMetadata(file));
        const indexedPosts = buildPostIndex(posts).posts;
        const loadedPostCache = indexedPosts.reduce((cache, post) => {
          if (post.markdown) {
            cache[post.content] = post;
          }

          return cache;
        }, {});

        if (!isMounted) return;

        setFolderStructure(nextFolderStructure);
        setAllPosts(indexedPosts);
        postCacheRef.current = loadedPostCache;
      } catch (error) {
        console.warn("Could not load blog index:", error);

        const fallbackFiles = flattenFiles(STATIC_FOLDER_STRUCTURE);
        const fallbackPosts = fallbackFiles.map((file) =>
          createPostMetadata(file),
        );

        if (!isMounted) return;

        setFolderStructure(STATIC_FOLDER_STRUCTURE);
        setAllPosts(buildPostIndex(fallbackPosts).posts);
        postCacheRef.current = {};
        setLoadError(
          "Some remote blog posts could not be loaded right now. Showing available local posts.",
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBlogIndexData();

    return () => {
      isMounted = false;
    };
  }, [createPostMetadata, loadRetryNonce]);

  useEffect(() => {
    const blogSidebarElement = document.querySelector(".blog-sidebar");
    const sidebarOverlayElement = document.querySelector(".sidebar-overlay");

    if (blogSidebarElement) {
      if (sidebarOpen) {
        blogSidebarElement.classList.add("open");
      } else {
        blogSidebarElement.classList.remove("open");
      }
    }

    if (sidebarOverlayElement) {
      sidebarOverlayElement.style.display = sidebarOpen ? "block" : "none";
    }

    return () => {
      if (blogSidebarElement) {
        blogSidebarElement.classList.remove("open");
      }
      if (sidebarOverlayElement) {
        sidebarOverlayElement.style.display = "none";
      }
    };
  }, [sidebarOpen]); // This effect runs whenever sidebarOpen state changes

  useEffect(() => {
    window.localStorage.setItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      String(sidebarCollapsed),
    );
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      BLOG_READING_THEATER_MODE_STORAGE_KEY,
      String(isTheaterMode),
    );
  }, [isTheaterMode]);

  useEffect(() => {
    const clampWidthForViewport = () => {
      setSidebarMaxWidth(getMaxSidebarWidth());
      setSidebarWidth((currentWidth) => getSafeSidebarWidth(currentWidth));
    };

    clampWidthForViewport();
    window.addEventListener("resize", clampWidthForViewport);

    return () => {
      window.removeEventListener("resize", clampWidthForViewport);
    };
  }, []);

  useEffect(() => {
    if (!isResizingSidebar) return undefined;

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handleMouseMove = (event) => {
      const sidebarElement = sidebarRef.current;
      if (!sidebarElement) return;

      const nextWidth = clampSidebarWidth(
        event.clientX - sidebarElement.getBoundingClientRect().left,
      );

      setSidebarWidth(nextWidth);
      window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(nextWidth));
    };

    const stopResizing = () => {
      setIsResizingSidebar(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopResizing);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizingSidebar]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const startSidebarResize = (event) => {
    if (
      sidebarCollapsed ||
      event.button !== 0 ||
      (typeof window !== "undefined" &&
        !window.matchMedia("(min-width: 769px)").matches)
    ) {
      return;
    }

    event.preventDefault();
    setIsResizingSidebar(true);
  };

  const resetSidebarWidth = () => {
    setSidebarWidth(DEFAULT_SIDEBAR_WIDTH);
    window.localStorage.setItem(
      SIDEBAR_WIDTH_STORAGE_KEY,
      String(DEFAULT_SIDEBAR_WIDTH),
    );
  };

  const returnToBlogList = () => {
    activePostRequestRef.current += 1;
    setSelectedPost(null);
    setPostLoading(false);
    setNotFoundMessage("");
    setPostError("");
    setActiveCategory(null);
    clearBlogUrl();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleContentClick = (e) => {
    if (
      window.innerWidth <= 769 && // Using 769px as per your CSS media query
      sidebarOpen &&
      e.target === e.currentTarget
    ) {
      setSidebarOpen(false);
    }
  };

  const toggleFolder = (path) => {
    setExpandedFolders((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const updatePostAfterLoad = useCallback((loadedPost) => {
    postCacheRef.current = {
      ...postCacheRef.current,
      [loadedPost.content]: loadedPost,
    };

    setAllPosts((prev) =>
      prev.map((post) =>
        post.content === loadedPost.content
          ? {
              ...post,
              title: loadedPost.title,
              slug: loadedPost.slug,
              markdownTitle: loadedPost.markdownTitle,
              markdown: loadedPost.markdown,
              source: loadedPost.source,
              pdfUrl: loadedPost.pdfUrl,
              contentMode: loadedPost.contentMode,
              language: loadedPost.language,
              preview: loadedPost.preview,
              wordCount: loadedPost.wordCount,
              description: loadedPost.description,
              summary: loadedPost.summary,
              date: loadedPost.date,
              tags: loadedPost.tags,
              categories: loadedPost.categories,
              displayCategory: loadedPost.displayCategory,
              readTime: loadedPost.readTime,
              hasFrontMatter: loadedPost.hasFrontMatter,
            }
          : post,
      ),
    );
  }, []);

  useEffect(() => {
    if (!selectedPost?.markdown || !pendingArticleHashRef.current) return;

    const hash = pendingArticleHashRef.current;
    pendingArticleHashRef.current = "";

    window.setTimeout(() => {
      scrollToArticleHash(`#${hash}`, contentRootRef.current);
    }, 0);
  }, [selectedPost]);

  const loadRoutePost = useCallback(
    async (file, category, filePath, articleHash = "", options = {}) => {
      const basePost = createPostMetadata({
        ...file,
        category,
        path: filePath,
      });

      const cachedPost = postCacheRef.current[basePost.content];
      const requestId = activePostRequestRef.current + 1;
      activePostRequestRef.current = requestId;

      setPostError("");
      setNotFoundMessage("");
      setSearchQuery("");
      setSelectedPost({ ...basePost, markdown: "" });
      setPostLoading(true);
      pendingArticleHashRef.current = articleHash || "";
      setExpandedFolders((prev) => ({
        ...prev,
        ...getExpandedFoldersForPath(filePath),
      }));

      if (cachedPost?.markdown || cachedPost?.source || cachedPost?.pdfUrl) {
        if (activePostRequestRef.current === requestId) {
          setSelectedPost(cachedPost);
          setPostLoading(false);

          if (options.replaceUrl && getPostUrl(cachedPost) !== location.pathname) {
            updateBlogUrl(cachedPost, { replace: true });
          }
        }

        if (window.innerWidth <= 769) {
          setSidebarOpen(false);
        }

        return;
      }

      if (window.innerWidth <= 769) {
        setSidebarOpen(false);
      }

      if (isPdfContentPost(basePost)) {
        const loadedPost = buildLoadedPostFromPdf(basePost);
        updatePostAfterLoad(loadedPost);

        if (activePostRequestRef.current === requestId) {
          setSelectedPost(loadedPost);
          setPostLoading(false);

          if (
            options.replaceUrl &&
            isShareableBlogPost(loadedPost) &&
            getPostUrl(loadedPost) !== location.pathname
          ) {
            updateBlogUrl(loadedPost, { replace: true });
          }
        }

        return;
      }

      try {
        const response = await fetch(buildCacheBustedUrl(basePost.content), {
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }

        const text = await response.text();
        const loadedPost =
          isSourceContentPost(basePost) && !isMarkdownContentPost(basePost)
            ? buildLoadedPostFromSource(basePost, text)
            : buildLoadedPostFromMarkdown(basePost, text);

        updatePostAfterLoad(loadedPost);

        if (activePostRequestRef.current === requestId) {
          setSelectedPost(loadedPost);
          if (
            options.replaceUrl &&
            isShareableBlogPost(loadedPost) &&
            getPostUrl(loadedPost) !== location.pathname
          ) {
            updateBlogUrl(loadedPost, { replace: true });
          }
        }
      } catch (error) {
        console.warn(`Could not load blog file ${basePost.title}:`, error);

        if (activePostRequestRef.current === requestId) {
          setPostError(
            `Could not load "${basePost.title}". Please try again later.`,
          );
        }
      } finally {
        if (activePostRequestRef.current === requestId) {
          setPostLoading(false);
        }
      }
    },
    [
      createPostMetadata,
      location.pathname,
      updateBlogUrl,
      updatePostAfterLoad,
    ],
  );

  const handleFileClick = async (file, category, filePath) => {
    const post = findPostFromUrlKey(file.githubPath || file.path || filePath);
    const targetPost = post || createPostMetadata({ ...file, category, path: filePath });

    navigate(getPostUrl(targetPost));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openMarkdownPath = (githubPath, articleHash = "") => {
    const post = markdownPathLookup[githubPath.toLowerCase()];

    if (!post) return;

    const hash = articleHash ? `#${encodeURIComponent(articleHash)}` : "";
    navigate(`${getPostUrl(post)}${hash}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (loading || allPosts.length === 0) return;

    const requestedPostKey = getRequestedPostKey();

    if (!requestedPostKey) {
      activePostRequestRef.current += 1;
      setNotFoundMessage("");
      setSelectedPost(null);
      setPostError("");
      setPostLoading(false);
      return;
    }

    const requestedPost = findPostFromUrlKey(requestedPostKey);

    if (!requestedPost) {
      activePostRequestRef.current += 1;
      setSelectedPost(null);
      setPostLoading(false);
      setPostError("");
      setNotFoundMessage(
        "Post not found. The blog post may have moved or been removed.",
      );
      return;
    }

    if (isNotesPost(requestedPost) && notesAreLocked) {
      activePostRequestRef.current += 1;
      pendingArticleHashRef.current = "";
      setSelectedPost(null);
      setPostLoading(false);
      setPostError("");
      setNotFoundMessage("");
      setSearchQuery("");
      setActiveCategory(NOTES_CATEGORY);
      setExpandedFolders((prev) => ({
        ...prev,
        ...getExpandedFoldersForPath(requestedPost.path),
      }));
      return;
    }

    loadRoutePost(
      requestedPost,
      requestedPost.category,
      requestedPost.path,
      location.hash ? location.hash.slice(1) : "",
      {
        replaceUrl:
          Boolean(new URLSearchParams(location.search).get("post")) ||
          location.pathname.toLowerCase() !== getPostUrl(requestedPost),
      },
    );
  }, [
    allPosts,
    findPostFromUrlKey,
    getRequestedPostKey,
    loadRoutePost,
    loading,
    location.hash,
    location.pathname,
    location.search,
    notesAreLocked,
  ]);

  const filterStructure = (structure, query, categoryFilter) => {
    let baseStructure = structure;
    if (categoryFilter) {
      const categoryMatch = structure.find(
        (item) =>
          (item.type === "folder" && item.name === categoryFilter) ||
          (item.type === "folder" &&
            item.children &&
            item.children.some(
              (child) =>
                child.type === "folder" && child.name === categoryFilter,
            )),
      );

      if (categoryMatch) {
        if (categoryMatch.name === categoryFilter) {
          baseStructure = [categoryMatch];
        } else {
          const categoryFolder = categoryMatch.children.find(
            (child) => child.type === "folder" && child.name === categoryFilter,
          );
          baseStructure = categoryFolder ? [categoryFolder] : [];
        }
      } else {
        baseStructure = [];
      }
    }

    return baseStructure
      .map((item) => {
        if (item.type === "file") {
          const searchableText = [
            item.name,
            item.githubPath,
            item.githubFolderPath,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchableText.includes(query) ? item : null;
        }

        const filteredChildren = item.children
          ? filterStructure(item.children, query, null)
          : [];

        if (
          item.name.toLowerCase().includes(query) ||
          filteredChildren.length > 0
        ) {
          return { ...item, children: filteredChildren };
        } else {
          return null;
        }
      })
      .filter(Boolean);
  };

  const visibleFolderStructure = useMemo(
    () => pruneLockedNotesTree(folderStructure, notesUnlocked),
    [folderStructure, notesUnlocked],
  );

  const structureToRender =
    searchQuery || activeCategory
      ? filterStructure(
          visibleFolderStructure,
          searchQuery.toLowerCase(),
          activeCategory,
        )
      : visibleFolderStructure;

  const renderFolder = (folder, path = "") => {
    const currentPath = path ? `${path}/${folder.name}` : folder.name;
    const isExpanded = expandedFolders[currentPath];

    const forceExpand =
      activeCategory &&
      folder.name !== activeCategory &&
      folder.children &&
      folder.children.some(
        (child) => child.type === "folder" && child.name === activeCategory,
      );

    return (
      <div key={currentPath} className="folder-container">
        <div className="folder-item" onClick={() => toggleFolder(currentPath)}>
          <span
            className={`folder-icon ${
              isExpanded || forceExpand ? "expanded" : ""
            }`}
          >
            {isExpanded || forceExpand ? "📂" : "📁"}
          </span>
          <span className="folder-name">{folder.name}</span>
        </div>
        {(isExpanded || forceExpand) && (
          <div className="folder-contents">
            {folder.children.map((item) =>
              item.type === "folder"
                ? renderFolder(item, currentPath)
                : (() => {
                    const filePath = `${currentPath}/${item.name}`;
                    const isSelected =
                      selectedPost?.path === filePath ||
                      (!selectedPost?.path &&
                        selectedPost?.id === item.content);

                    return (
                      <div
                        key={filePath}
                        className={`file-item ${isSelected ? "active" : ""}`}
                        onClick={() =>
                          handleFileClick(
                            item,
                            getCategoryFromPath(currentPath),
                            filePath,
                          )
                        }
                      >
                        <span className="file-icon">
                          {isPdfContentPost(item) ? "📕" : "📄"}
                        </span>
                        <span className="file-name">{item.name}</span>
                      </div>
                    );
                  })(),
            )}
          </div>
        )}
      </div>
    );
  };

  const getReadingTime = (post) => {
    if (post?.contentMode === "pdf" || isPdfContentPost(post)) return "PDF note";
    if (post?.readTime) return `${post.readTime} min read`;
    return post?.wordCount
      ? `${Math.ceil(post.wordCount / 200)} min read`
      : "Open to read";
  };

  const getPostCountLabel = (count) =>
    `${count} ${count === 1 ? "post" : "posts"}`;

  const openPost = (post) => {
    navigate(getPostUrl(post));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderBlogCard = (post) => (
    <div
      key={post.id}
      className="blog-card"
      onClick={() => openPost(post)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openPost(post);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Open ${post.title}`}
    >
      {(post.displayCategory || post.category) && (
        <span className="category-tag">
          {post.displayCategory || post.category}
        </span>
      )}
      <h3>{post.title}</h3>
      <p>{post.preview}</p>
      {post.date && <span className="reading-time">{post.date}</span>}
      <span className="reading-time">{getReadingTime(post)}</span>
    </div>
  );

  const renderNotesLockPanel = () => (
    <section className="blog-lock-panel" aria-labelledby="notes-lock-title">
      <div className="blog-lock-card">
        <span className="blog-lock-eyebrow">Private Notes</span>
        <h2 id="notes-lock-title" className="blog-lock-title">
          Private Study Notes
        </h2>
        <p className="blog-lock-description">
          Enter the password to view your saved notes and study documents.
        </p>
        <form className="blog-lock-form" onSubmit={handleNotesUnlock}>
          <label htmlFor="notes-password" className="sr-only">
            Notes password
          </label>
          <input
            id="notes-password"
            className="blog-lock-input"
            type="password"
            value={notesPasswordInput}
            onChange={(event) => {
              setNotesPasswordInput(event.target.value);
              setNotesUnlockError("");
            }}
            placeholder="Password"
            aria-label="Notes password"
            autoComplete="current-password"
          />
          <button className="blog-lock-button" type="submit">
            Unlock Notes
          </button>
        </form>
        {notesUnlockError && (
          <p className="blog-lock-error" role="alert">
            {notesUnlockError}
          </p>
        )}
      </div>
    </section>
  );

  const renderCardGrid = () => {
    if (activeCategory === NOTES_CATEGORY && notesAreLocked) {
      return renderNotesLockPanel();
    }

    const displayedPosts = activeCategory
      ? allPosts.filter((post) => postMatchesCategory(post, activeCategory))
      : notesAreLocked
        ? allPosts.filter((post) => !isNotesPost(post))
        : allPosts;

    const searchedAndFilteredPosts = searchQuery
      ? displayedPosts.filter((post) =>
          [
            post.title,
            post.summary,
            post.description,
            post.date,
            post.category,
            post.displayCategory,
            post.path,
            post.githubPath,
            post.markdown,
            ...(post.categories || []),
            ...(post.tags || []),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
        )
      : displayedPosts;

    if (searchedAndFilteredPosts.length === 0) {
      return (
        <p className="blog-empty-message">
          No posts found matching your criteria in this category or search.
        </p>
      );
    }

    const groupedPosts = searchedAndFilteredPosts.reduce((groups, post) => {
      const category = post.category || "Uncategorized";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(post);
      return groups;
    }, {});

    return (
      <div className="blog-category-sections">
        {Object.entries(groupedPosts).map(([category, posts]) => (
          <section className="blog-category-section" key={category}>
            <div className="blog-category-heading">
              <h2>{category}</h2>
              <span>{getPostCountLabel(posts.length)}</span>
            </div>
            <div className="blog-card-grid">
              {posts.map((post) => renderBlogCard(post))}
            </div>
          </section>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="blog-loading blog-loading-page" aria-live="polite">
        <div className="blog-loading-header">
          <div className="loading-spinner"></div>
          <p>Loading blog posts...</p>
        </div>
        <div className="blog-skeleton-grid" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, index) => (
            <div className="blog-card blog-card-skeleton" key={index}>
              <span className="skeleton-pill" />
              <span className="skeleton-line skeleton-title" />
              <span className="skeleton-line" />
              <span className="skeleton-line short" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const categories = Array.from(
    allPosts
      .map((post) =>
        isNotesPost(post) || isNotesCategoryValue(post.category)
          ? NOTES_CATEGORY
          : post.category,
      )
      .concat(NOTES_CATEGORY)
      .reduce((uniqueCategories, category) => {
        if (category && !uniqueCategories.has(category)) {
          uniqueCategories.set(category, category);
        }
        return uniqueCategories;
      }, new Map())
      .values(),
  );
  const isMarkdownReadingPost =
    selectedPost &&
    selectedPost.contentMode !== "pdf" &&
    selectedPost.contentMode !== "source";
  const readingThemeStyle =
    isMarkdownReadingPost && selectedTheme
      ? {
          "--reading-bg": selectedTheme.background,
          "--reading-surface": selectedTheme.surface,
          "--reading-text": selectedTheme.text,
          "--reading-muted": selectedTheme.muted,
          "--reading-heading": selectedTheme.heading,
          "--reading-link": selectedTheme.link,
          "--reading-border": selectedTheme.border,
        }
      : undefined;

  return (
    <div
      className={`blog-layout ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}
    >
      <button
        className={`sidebar-toggle ${sidebarOpen ? "open" : ""}`}
        onClick={toggleSidebar}
        aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
      >
        {sidebarOpen ? "✕" : "☰"}
      </button>

      <div className="sidebar-overlay" onClick={toggleSidebar} />

      <div
        ref={sidebarRef}
        className={`blog-sidebar ${sidebarOpen ? "open" : ""} ${
          sidebarCollapsed ? "collapsed" : ""
        }`}
        style={
          sidebarCollapsed
            ? undefined
            : {
                "--blog-sidebar-width": `${sidebarWidth}px`,
                "--blog-sidebar-max-width": `${sidebarMaxWidth}px`,
              }
        }
      >
        <div className="sidebar-header">
          <div className="sidebar-title-row">
            <h3
              onClick={() => {
                activePostRequestRef.current += 1;
                setSelectedPost(null);
                setPostLoading(false);
                setNotFoundMessage("");
                setPostError("");
                setActiveCategory(null);
                setSearchQuery("");
                setExpandedFolders({});
                clearBlogUrl();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              style={{ cursor: "pointer" }}
            >
              Documentation
            </h3>
            <button
              type="button"
              className="sidebar-collapse-btn"
              onClick={toggleSidebarCollapsed}
              aria-label={
                sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? "›" : "‹"}
            </button>
          </div>
          <div className="sidebar-search">
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedPost(null);
                setNotFoundMessage("");
                setPostError("");
                clearBlogUrl();
              }}
            />
          </div>
          <div className="sidebar-filters">
            <button
              className={`filter-btn ${
                activeCategory === null ? "active" : ""
              }`}
              onClick={() => {
                setActiveCategory(null);
                setSelectedPost(null);
                setNotFoundMessage("");
                setPostError("");
                setSearchQuery("");
                setExpandedFolders({});
                clearBlogUrl();
              }}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category}
                className={`filter-btn ${
                  activeCategory === category ? "active" : ""
                }`}
                onClick={() => {
                  setActiveCategory(category);
                  setSelectedPost(null);
                  setNotFoundMessage("");
                  setPostError("");
                  setSearchQuery("");
                  clearBlogUrl();
                  setExpandedFolders((prev) => {
                    const newState = {};
                    const findAndExpand = (structure, currentPath = "") => {
                      structure.forEach((item) => {
                        const itemPath = currentPath
                          ? `${currentPath}/${item.name}`
                          : item.name;
                        if (item.type === "folder") {
                          if (item.name === category) {
                            newState[itemPath] = true;
                            let parentPath = currentPath;
                            while (parentPath) {
                              newState[parentPath] = true;
                              parentPath = parentPath.substring(
                                0,
                                parentPath.lastIndexOf("/"),
                              );
                            }
                          } else if (item.children) {
                            findAndExpand(item.children, itemPath);
                          }
                        }
                      });
                    };
                    findAndExpand(folderStructure);
                    return newState;
                  });
                }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        <div className="folder-structure">
          {structureToRender.map((folder) => renderFolder(folder))}
        </div>
        <div
          className="sidebar-resize-handle"
          onMouseDown={startSidebarResize}
          onDoubleClick={resetSidebarWidth}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          title="Drag to resize sidebar"
        />
      </div>

      <div className="blog-content" onClick={handleContentClick}>
        {loadError && !selectedPost && (
          <div className="blog-fallback-message" role="status">
            <p>{loadError}</p>
            <button
              type="button"
              className="filter-btn"
              onClick={() => setLoadRetryNonce((nonce) => nonce + 1)}
            >
              Retry
            </button>
          </div>
        )}
        {notFoundMessage && !selectedPost && (
          <p className="blog-error-message">{notFoundMessage}</p>
        )}
        {selectedPost ? (
          <div
            className={`post-container ${
              selectedPost.contentMode === "pdf" ? "pdf-post-container" : ""
            } ${
              isMarkdownReadingPost && isTheaterMode
                ? "post-container--theater"
                : ""
            }`}
          >
            <div className="post-nav-controls" aria-label="Blog post navigation">
              <button
                type="button"
                className="post-nav-button"
                onClick={returnToBlogList}
              >
                ← Back to Blogs
              </button>
            </div>
            <div
              className={isMarkdownReadingPost ? "blog-reading-surface" : ""}
              style={readingThemeStyle}
            >
              {!postLoading && isMarkdownReadingPost && (
                <div className="blog-reading-tools">
                  <BlogVoiceReader
                    contentRootRef={contentRootRef}
                    postId={selectedPost.slug || selectedPost.id}
                    disabled={postLoading}
                  />
                  <BlogReadingThemePicker
                    themes={BLOG_READING_THEMES}
                    selectedThemeId={selectedThemeId}
                    onThemeChange={setSelectedThemeId}
                    isTheaterMode={isTheaterMode}
                    onTheaterModeChange={setIsTheaterMode}
                  />
                </div>
              )}
              <div className="post-header">
                <h1>{selectedPost.title}</h1>
                <div className="post-meta">
                  {selectedPost.date && (
                    <span className="post-date">{selectedPost.date}</span>
                  )}
                  <span className="post-reading-time">
                    {getReadingTime(selectedPost)} {/* Reading time */}
                  </span>
                </div>
              </div>
              {postError && <p className="blog-error-message">{postError}</p>}
              {postLoading ? (
                <div className="blog-loading blog-loading-inline">
                  <div className="loading-spinner"></div>
                  <p>Loading post...</p>
                </div>
              ) : (
                <article className="blog-post" ref={contentRootRef}>
                  {selectedPost.contentMode === "pdf" ? (
                    <PdfRenderer
                      title={selectedPost.title}
                      url={
                        selectedPost.pdfUrl ||
                        selectedPost.rawUrl ||
                        selectedPost.content
                      }
                    />
                  ) : selectedPost.contentMode === "source" ? (
                    <SourceRenderer
                      source={selectedPost.source || ""}
                      language={
                        selectedPost.language || getPostLanguage(selectedPost)
                      }
                    />
                  ) : (
                    <MarkdownRenderer
                      markdown={selectedPost.markdown}
                      baseUrl={selectedPost.baseUrl}
                      currentGithubPath={selectedPost.githubPath}
                      contentRootRef={contentRootRef}
                      markdownPathLookup={markdownPathLookup}
                      notesUnlocked={notesUnlocked}
                      onOpenMarkdownPath={openMarkdownPath}
                    />
                  )}
                </article>
              )}
              <div className="post-footer">
                <div className="post-tags">
                  <span>Tags:</span>
                  {(selectedPost.tags?.length
                    ? selectedPost.tags
                    : [
                        ...(selectedPost.categories || []),
                        selectedPost.displayCategory || selectedPost.category,
                        "Documentation",
                        "Guide",
                      ].filter(Boolean)
                  ).map((tag) => (
                    <span className="tag" key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          renderCardGrid()
        )}
      </div>
    </div>
  );
};

export default BlogPost;
